import React, { useState, useMemo, useCallback } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { notification, Radio, Space, Alert, Button } from "antd";
import clientApi from "../../store/api";

const ClientPaymentForm = ({
  clientSecret,
  paymentFormRef,
  clientId,
  onClose,
  setIsLoading,
  setFormSubmitted,
  onSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [formError, setFormError] = useState(null);
  const [cardholderName, setCardholderName] = useState("");
  const [paymentMethodType, setPaymentMethodType] = useState("card");

  const handleClearFields = useCallback(() => {
    if (elements && paymentMethodType === "card") {
      const cardElement = elements.getElement(CardElement);
      if (cardElement) {
        cardElement.clear();
      }
    }
    setCardholderName("");
  }, [elements, paymentMethodType]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setIsLoading(true);
      setFormSubmitted(true);
      setFormError(null);

      if (!stripe || !elements) {
        setIsLoading(false);
        setFormSubmitted(false);
        return;
      }

      // For bank account and cashapp, redirect to Stripe portal
      if (paymentMethodType === "us_bank_account" || paymentMethodType === "cashapp") {
        try {
          const response = await clientApi.createClientPortalSession(clientId);
          window.open(response.url, "_blank");
          onClose();
        } catch (error) {
          console.error("Error creating Stripe portal session:", error);
          notification.error({
            message: "Failed to open Stripe portal"
          });
        }
        setIsLoading(false);
        setFormSubmitted(false);
        return;
      }

      let result;

      if (paymentMethodType === "card") {
        result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: cardholderName
            }
          }
        });
      }

      if (result.error) {
        setIsLoading(false);
        setFormSubmitted(false);
        setFormError(
          "There was a problem creating the payment method. Please try again."
        );
        notification.error({
          message: "Payment method creation failed",
          description: result.error.message
        });
      } else if (result.setupIntent && result.setupIntent.status === "requires_action") {
        setIsLoading(false);
        setFormSubmitted(false);
        notification.info({
          message: "Payment Method Added - Verification Required",
          description:
            "Payment method has been added but may require additional verification."
        });
        handleClearFields();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        try {
          // Auto-set new payment method as default for client
          await clientApi.setClientDefaultPaymentMethod(
            clientId,
            result.setupIntent.payment_method
          );

          notification.success({
            message: "Payment Method Added Successfully!",
            description:
              "The payment method has been added and set as default for this client."
          });
          handleClearFields();
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        } catch (err) {
          console.error("Error setting default payment method:", err);
          setIsLoading(false);
          setFormSubmitted(false);
          notification.error({
            message: "Payment method added but failed to set as default",
            description: "Please manually set it as default in the payment methods list."
          });
          if (onSuccess) {
            onSuccess();
          }
        }
      }
    },
    [
      stripe,
      elements,
      clientSecret,
      cardholderName,
      paymentMethodType,
      clientId,
      handleClearFields,
      onClose,
      setIsLoading,
      setFormSubmitted,
      onSuccess
    ]
  );

  const style = useMemo(
    () => ({
      base: {
        color: "#000000",
        fontSize: "16px",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        "::placeholder": {
          color: "#cccccc"
        }
      },
      invalid: {
        color: "#df1b41"
      }
    }),
    []
  );

  return (
    <form onSubmit={handleSubmit} ref={paymentFormRef} style={{ width: "100%" }}>
      <div style={{ marginBottom: "20px" }}>
        <Radio.Group
          value={paymentMethodType}
          onChange={(e) => setPaymentMethodType(e.target.value)}
        >
          <Space direction="vertical">
            <Radio value="card">Credit/Debit Card</Radio>
            <Radio value="us_bank_account">Bank Account (ACH)</Radio>
            <Radio value="cashapp">Cash App Pay</Radio>
          </Space>
        </Radio.Group>
      </div>

      {paymentMethodType === "card" && (
        <>
          <div style={{ marginBottom: "15px" }}>
            <input
              type="text"
              id="cardholder-name"
              value={cardholderName}
              placeholder="Name on Card"
              onChange={(e) => {
                const value = e.target.value;
                const filteredValue = value.replace(/[^\p{L}\s]/gu, "");
                setCardholderName(filteredValue);
              }}
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                fontSize: "16px"
              }}
            />
          </div>
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              padding: "12px",
              marginBottom: "15px"
            }}
          >
            <CardElement options={{ style }} />
          </div>
        </>
      )}

      {paymentMethodType === "us_bank_account" && (
        <Alert
          message="Bank Account Management"
          description={
            <div>
              <p>
                Bank accounts must be managed directly in Stripe for security and
                verification purposes.
              </p>
              <Button
                type="primary"
                onClick={() => {
                  const confirmed = window.confirm(
                    "The client will be redirected to Stripe to manage their bank accounts.\n\nPlease note: It may take up to 2 minutes for Stripe to show all subscriptions and payment methods.\n\nDo you want to continue?"
                  );

                  if (!confirmed) {
                    return;
                  }

                  clientApi
                    .createClientPortalSession(clientId)
                    .then((response) => {
                      window.open(response.url, "_blank");
                      onClose();
                    })
                    .catch(() => {
                      notification.error({
                        message: "Failed to open Stripe portal"
                      });
                    });
                }}
              >
                Manage in Stripe ↗
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />
      )}

      {paymentMethodType === "cashapp" && (
        <Alert
          message="Cash App Pay"
          description={
            <div>
              <p>
                Cash App Pay must be set up directly in Stripe for secure payment
                processing.
              </p>
              <Button
                type="primary"
                onClick={() => {
                  const confirmed = window.confirm(
                    "The client will be redirected to Stripe to set up Cash App Pay.\n\nPlease note: It may take up to 2 minutes for Stripe to show all subscriptions and payment methods.\n\nDo you want to continue?"
                  );

                  if (!confirmed) {
                    return;
                  }

                  clientApi
                    .createClientPortalSession(clientId)
                    .then((response) => {
                      window.open(response.url, "_blank");
                      onClose();
                    })
                    .catch(() => {
                      notification.error({
                        message: "Failed to open Stripe portal"
                      });
                    });
                }}
              >
                Set up in Stripe ↗
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />
      )}

      {formError && <div style={{ color: "red", marginBottom: "15px" }}>{formError}</div>}

      {paymentMethodType === "card" && (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            marginTop: "20px",
            fontSize: "12px",
            color: "#666"
          }}
        >
          Payment method will be added on behalf of the client and set as their default
          method.
        </div>
      )}
    </form>
  );
};

export default ClientPaymentForm;
